import * as React from "react";
import { useRef, useState } from "react";
import moment from "moment";
import styled from "styled-components";
import { BootstrapModalForm } from "components/bootstrap";
import { TimeUnitInput } from "components/common";
import Spinner from "components/common/Spinner";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import UserNotification from "util/UserNotification";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { addWatchlistEntries, useWatchlist, validateWatchlistEntries } from "watchlist/hooks/useWatchlistsV2";
import WatchlistEntryValuesInput from "./WatchlistEntryValuesInput";
const TTL_UNITS = ["DAYS", "HOURS", "MINUTES", "SECONDS"];
const Description = styled.p(
  ({ theme }) => `
  font-size: ${theme.fonts.size.small};
  color: ${theme.colors.gray[40]};
`
);
const keyOf = (values) => values.join("\n");
const AddWatchlistEntriesModal = ({ watchlistId, watchlistName, onClose, onAdded }) => {
  var _a;
  const { data: watchlist } = useWatchlist(watchlistId);
  const [values, setValues] = useState([]);
  const valuesRef = useRef([]);
  const [override, setOverride] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [invalid, setInvalid] = useState({});
  const [lastValidatedKey, setLastValidatedKey] = useState(null);
  const validationRequest = useRef(null);
  const sendTelemetry = useSendTelemetry();
  const defaultTtl = watchlist ? extractDurationAndUnit(((_a = watchlist.default_ttl_seconds) != null ? _a : 0) * 1e3, TTL_UNITS) : null;
  const ttl = override != null ? override : defaultTtl;
  const handleTtlChange = (duration, unit) => setOverride({ duration, unit });
  const validate = (list) => {
    var _a2;
    const key = keyOf(list);
    if (((_a2 = validationRequest.current) == null ? void 0 : _a2.key) === key) {
      return validationRequest.current.result;
    }
    if (list.length === 0 || !(watchlist == null ? void 0 : watchlist.type)) {
      return Promise.resolve({});
    }
    const result = validateWatchlistEntries(watchlist.type, list).then(({ invalid: reported }) => {
      var _a3;
      if (((_a3 = validationRequest.current) == null ? void 0 : _a3.key) === key) {
        setInvalid(reported);
        setLastValidatedKey(key);
      }
      return reported;
    }).catch((error) => {
      UserNotification.error(`Validating entries failed: ${error}`);
      validationRequest.current = null;
      return {};
    });
    validationRequest.current = { key, result };
    return result;
  };
  const handleValuesChange = (next) => {
    setValues(next);
    valuesRef.current = next;
    setInvalid({});
    setLastValidatedKey(null);
    validationRequest.current = null;
    validate(next);
  };
  const handleSubmit = async () => {
    const list = valuesRef.current;
    if (list.length === 0) {
      UserNotification.error("Enter at least one value to add.");
      return;
    }
    if (ttl === null) {
      return;
    }
    const ttlSeconds = Math.round(
      moment.duration(ttl.duration, ttl.unit.toLowerCase()).asSeconds()
    );
    if (!Number.isFinite(ttlSeconds) || ttlSeconds < 0) {
      UserNotification.error("TTL must be a non-negative duration.");
      return;
    }
    setSubmitting(true);
    const reported = keyOf(list) === lastValidatedKey ? invalid : await validate(list);
    if (Object.keys(reported).length > 0) {
      setSubmitting(false);
      return;
    }
    addWatchlistEntries(watchlistId, list, ttlSeconds).then((result) => {
      var _a2;
      const errorEntries = Object.entries((_a2 = result.errors) != null ? _a2 : {});
      if (result.added_count > 0) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.WATCHLIST.ENTRIES_ADDED, {
          app_section: "watchlist-details",
          app_action_value: "watchlist-entries-added",
          event_details: { watchlist_name: watchlistName, entry_count: result.added_count }
        });
        UserNotification.success(
          `Added ${result.added_count} entr${result.added_count === 1 ? "y" : "ies"} to "${watchlistName}".`
        );
      }
      if (errorEntries.length > 0) {
        UserNotification.error(
          `${errorEntries.length} value${errorEntries.length === 1 ? "" : "s"} w${errorEntries.length === 1 ? "as" : "ere"} rejected: ${errorEntries.map(([value, message]) => `${value} (${message})`).join("; ")}`
        );
      }
      onAdded();
      onClose();
    }).catch((error) => {
      UserNotification.error(`Adding entries failed: ${error}`);
    }).finally(() => {
      setSubmitting(false);
    });
  };
  const invalidCount = Object.keys(invalid).length;
  return /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: true,
      title: `Add entries to "${watchlistName}"`,
      onSubmitForm: handleSubmit,
      onCancel: onClose,
      submitButtonText: submitting ? "Adding\u2026" : "Add entries",
      submitButtonDisabled: submitting || ttl === null || invalidCount > 0
    },
    (watchlist == null ? void 0 : watchlist.description) && /* @__PURE__ */ React.createElement(Description, null, watchlist.description),
    /* @__PURE__ */ React.createElement(
      WatchlistEntryValuesInput,
      {
        values,
        invalid,
        onChange: handleValuesChange,
        error: invalidCount > 0 ? `${invalidCount} value${invalidCount === 1 ? "" : "s"} rejected - hover a highlighted value for the reason, then remove and fix it.` : void 0
      }
    ),
    ttl === null ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading watchlist default\u2026" }) : /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: "TTL",
        required: true,
        update: handleTtlChange,
        value: ttl.duration,
        unit: ttl.unit,
        units: TTL_UNITS,
        help: "How long newly added entries are kept. Pre-filled with the watchlist's default; leave it to apply the default. Set to 0 to never expire."
      }
    )
  );
};
export default AddWatchlistEntriesModal;
